import { yupResolver } from "@hookform/resolvers/yup"
import { useQueryClient } from "@tanstack/react-query"
import { useEffect, useMemo, useState } from "react"
import { FormProvider, useForm } from "react-hook-form"
import { DashboardUpdateCreatePayload } from "../../../types/macroApi"
import { useQueryProfile } from "../../hooks"
import { toaster } from "../../utils/createToaster"
import {
  CellStyleProtocol,
  ScientValueFormatter,
} from "../PortfolioManagement/components/agGridMapping"
import { DataframeData } from "../PortfolioManagement/types"
import DatavizChart from "./components/DatavizChart"
import { DatavizHeader } from "./components/DatavizHeader"
import DatavizTable from "./components/DatavizTable"
import { BottomContainer, DatavizContainer } from "./Dataviz.styled"
import {
  useDashboardDataQuery,
  useDashboardMutation,
  useDashboardsQuery,
  useDashboardUpdateMutation,
  useRunDashboardPredictionMutation,
} from "./hooks/useDashboardQueries"
import { DashboardUpdateCreateSchema } from "./schemas/dashboard.schema"
import { Dashboard, DashboardItem, Frequency, Source } from "./types/dashboard"
import { DashboardItemForm, DashboardUpdateCreateForm } from "./types/dashboardForm"

const Dataviz = () => {
  const [showChart, setShowChart] = useState(false)
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null | undefined>(
    undefined,
  )

  const { data: profile } = useQueryProfile()
  const queryClient = useQueryClient()

  const methods = useForm<DashboardUpdateCreateForm>({
    defaultValues: {
      name: "",
      items: [],
      zScore: false,
      backtest: true,
      source: Source.OBSERVED,
      freq: Frequency.MONTHLY,
    },
    resolver: yupResolver(DashboardUpdateCreateSchema),
  })

  const {
    setValue,
    watch,
    reset,
    getValues,
    formState: { dirtyFields },
  } = methods

  // Only consider data-related fields dirty (not name changes)
  const isDataDirty = Boolean(
    dirtyFields.items || dirtyFields.zScore || dirtyFields.backtest || dirtyFields.source || dirtyFields.freq,
  )

  const { items, zScore } = watch()

  const { dashboardData, isFetching: isFetchingData } = useDashboardDataQuery(
    selectedDashboard?.id || null,
  )
  const { mutate: createDashboard, isLoading: isLoadingCreateDashboard } = useDashboardMutation()
  const { mutate: updateDashboard, isLoading: isLoadingUpdateDashboard } =
    useDashboardUpdateMutation()
  const { dashboards } = useDashboardsQuery()
  const isSaving = isLoadingCreateDashboard || isLoadingUpdateDashboard
  const { mutate: runPrediction } = useRunDashboardPredictionMutation()

  /**
   * Build a dataframe with the selected datasets and their names
   */
  const displayedData = useMemo((): DataframeData => {
    if ((!isFetchingData && isDataDirty) || !dashboardData?.data) {
      // Build empty temp dataframe with single "Dataset" column showing dataset names
      return {
        rows: (items as DashboardItemForm[])?.map(item => {
          if (item.company_id) {
            const label = `${item?.company_main_instrument_ticker?.replace(" Equity", "")} ${
              item.fundamentals_metric
            } ${item.fundamentals_source} (${item.fundamentals_periodicity})`
            return {
              metric: label,
              query_key: `funda:${item.company_id}|${item.fundamentals_metric}|${item.fundamentals_source}|${item.fundamentals_periodicity}`,
            }
          }
          return {
            metric: item.dataset_long_name || item.dataset_name || "",
            query_key: `dataset:${item.dataset_id}`,
          }
        }),
        columns: [
          {
            field: "metric",
            headerName: "Metric",
            valueFormatter: ScientValueFormatter.TEXT,
            cellStyles: [{ style: CellStyleProtocol.WHITE_BOLD }],
          },
          {
            field: "query_key",
            valueFormatter: ScientValueFormatter.TEXT,
            cellStyles: [{ style: CellStyleProtocol.WHITE }],
            hide: true, // Hidden column for ID
          },
        ],
      }
    } else {
      return dashboardData?.data
    }
  }, [dashboardData?.data, items, isDataDirty, isFetchingData])

  const handleDashboardSelect = (dashboard: Dashboard | null) => {
    setSelectedDashboard(dashboard)
    if (dashboard) {
      reset({
        name: dashboard.name,
        items: dashboard.items,
        zScore: dashboard.z_score,
        backtest: dashboard.backtest,
        source: dashboard.source,
        freq: dashboard.freq,
      })
    } else {
      reset({
        name: "",
        items: [],
        zScore: false,
        backtest: true,
        source: Source.OBSERVED,
        freq: Frequency.MONTHLY,
      })
    }
  }

  const handleSave = () => {
    if (!profile) return

    const dashboardFormData = getValues()

    if (selectedDashboard) {
      // PATCH: only send fields that changed
      const payload: DashboardUpdateCreatePayload = {}

      if (dirtyFields.name) {
        payload.name = dashboardFormData.name
      }
      if (dirtyFields.zScore) {
        payload.z_score = dashboardFormData.zScore
      }
      if (dirtyFields.backtest) {
        payload.backtest = dashboardFormData.backtest
      }
      if (dirtyFields.source) {
        payload.source = dashboardFormData.source
      }
      if (dirtyFields.freq) {
        payload.freq = dashboardFormData.freq
      }
      // Only send items if they changed - this preserves item IDs on backend
      if (dirtyFields.items) {
        payload.items = dashboardFormData.items as DashboardItem[]
      }

      const dataChanged =
        dirtyFields.items || dirtyFields.zScore || dirtyFields.backtest || dirtyFields.source || dirtyFields.freq

      if (dataChanged) {
        // Clear data immediately - this triggers empty layout via !dashboardData?.data
        queryClient.setQueryData(["dashboard", selectedDashboard.id], (old: any) => {
          if (!old) return old
          return { ...old, data: null }
        })
      }

      updateDashboard(
        { id: selectedDashboard.id, data: payload },
        {
          onSuccess: updatedDashboard => {
            handleDashboardSelect(updatedDashboard)

            // Only auto-refetch for OBSERVED mode
            if (dataChanged && updatedDashboard.source !== Source.PREDICTED) {
              queryClient.invalidateQueries({ queryKey: ["dashboard", updatedDashboard.id] })
            }
            // For PREDICTED: do nothing, user will click refresh button when ready

            toaster.show({
              message: "Dashboard updated successfully",
              intent: "success",
            })
          },
          onError: () => {
            toaster.show({
              message: "Failed to update dashboard",
              intent: "danger",
            })
          },
        },
      )
    } else {
      // CREATE: send all fields
      const payload: DashboardUpdateCreatePayload = {
        name: dashboardFormData.name,
        items: dashboardFormData.items as DashboardItem[],
        z_score: dashboardFormData.zScore,
        backtest: dashboardFormData.backtest,
        source: dashboardFormData.source,
        freq: dashboardFormData.freq,
      }
      createDashboard(payload, {
        onSuccess: newDashboard => {
          handleDashboardSelect(newDashboard)
          toaster.show({
            message: "Dashboard saved successfully",
            intent: "success",
          })
        },
        onError: () => {
          toaster.show({
            message: "Failed to save dashboard",
            intent: "danger",
          })
        },
      })
    }
  }

  /**
   * Set form values from selected dashboard data once we get all the dashboard detail.
   */
  useEffect(() => {
    if (dashboardData?.items) {
      setValue("items", dashboardData.items, { shouldDirty: false })
    }
  }, [dashboardData, setValue])

  /**
   * Auto-select first dashboard if nothing is selected
   */
  useEffect(() => {
    if (dashboards && dashboards.length > 0 && selectedDashboard === undefined) {
      handleDashboardSelect(dashboards[0])
    }
  }, [dashboards, selectedDashboard, handleDashboardSelect])

  const emptyDataframeData: DataframeData = {
    rows: [],
    columns: [],
  }

  const hasData = displayedData && displayedData.rows?.length > 0
  const hasDatasets = items?.length > 0
  const isDashboardOwner = selectedDashboard?.owner.username === profile?.username

  const handleRunPrediction = () => {
    if (!selectedDashboard) return
    runPrediction(
      { id: selectedDashboard.id },
      {
        onSuccess: () => toaster.show({ message: "Prediction enqueued", intent: "success" }),
        onError: () => toaster.show({ message: "Failed to enqueue prediction", intent: "danger" }),
      },
    )
  }

  const handleRefresh = () => {
    if (!selectedDashboard) return
    queryClient.invalidateQueries({ queryKey: ["dashboard", selectedDashboard.id] })
  }

  return (
    <FormProvider {...methods}>
      <DatavizContainer flexDirection="column" gap="20px" alignItems="center">
        <DatavizHeader
          onDashboardSelect={handleDashboardSelect}
          onSave={handleSave}
          onRefresh={handleRefresh}
          showChart={showChart}
          onViewChange={setShowChart}
          hasData={hasData || false}
          handleDashboardSelect={handleDashboardSelect}
          isLoading={isFetchingData}
          isSaving={isSaving}
          canRunPrediction={true}
          onRunPrediction={handleRunPrediction}
          selectedDashboard={selectedDashboard}
        />
        <BottomContainer className="ag-theme-balham-dark" flexGrow={1} flexDirection="column">
          {hasData ? (
            showChart ? (
              <DatavizChart apiData={displayedData} zScore={zScore} />
            ) : (
              <DatavizTable apiData={displayedData} />
            )
          ) : hasDatasets ? (
            <DatavizTable apiData={emptyDataframeData} />
          ) : (
            <DatavizTable apiData={emptyDataframeData} />
          )}
        </BottomContainer>
      </DatavizContainer>
    </FormProvider>
  )
}

export default Dataviz
